import React from 'react';

// The three button treatments:
//  - primary:   solid charcoal (#292927), white label
//  - secondary: muted grey fill (rgb(239,239,239)), charcoal label — plugin
//               downloads, small "Download" / "Add" actions
//  - outline:   transparent with a hairline border — "Continue Shopping", "Sign Out"
type Variant = 'primary' | 'secondary' | 'outline';

// Heights: 48 (mobile bar), 44 (in-card desktop); `card` is padding-driven
// (~38px) / 13px text to match the in-flow buttons ("Manage Subscription",
// plugin downloads) exactly; 32 (small), 28 (chip).
type Size = 'lg' | 'md' | 'card' | 'sm' | 'chip';

// Charcoal #292927 (`ink-secondary`), not pure black. Primary dims to 90% on
// hover (the plugin's approach); the grey secondary is too light for an opacity
// shift to read, so it darkens its fill instead.
const VARIANTS: Record<Variant, string> = {
    primary: 'bg-ink-secondary text-white hover:opacity-90',
    secondary: 'bg-surface-muted text-ink-secondary hover:bg-surface-muted-hover',
    outline: 'bg-transparent text-ink-secondary border border-hairline hover:border-ink-secondary/40',
};

const SIZES: Record<Size, string> = {
    lg: 'h-12 px-5 rounded-btn text-ui',
    md: 'h-11 px-5 rounded-btn text-ui',
    // padding-driven ~38px; `text-r-sub` carries line-height:normal, which
    // cancels the inherited 1.5 that would otherwise pad the box out.
    card: 'px-[18px] py-[11px] rounded-btn text-r-sub',
    sm: 'h-8 px-4 rounded-chip text-meta',
    chip: 'h-7 px-3 rounded-chip text-meta',
};

type CommonProps = {
    variant?: Variant;
    size?: Size;
    fullWidth?: boolean;
    className?: string;
    children: React.ReactNode;
};

type ButtonProps = CommonProps &
    Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'className' | 'children'> & { href?: undefined };

type AnchorProps = CommonProps &
    Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, 'className' | 'children'> & { href: string };

function classes(variant: Variant, size: Size, fullWidth: boolean, className: string) {
    return [
        'inline-flex items-center justify-center font-semibold whitespace-nowrap',
        'transition disabled:opacity-50 disabled:cursor-not-allowed',
        VARIANTS[variant],
        SIZES[size],
        fullWidth ? 'w-full' : '',
        className,
    ]
        .filter(Boolean)
        .join(' ');
}

/**
 * The site's button. Renders an `<a>` when `href` is set (plugin / signed
 * downloads), a `<button>` otherwise.
 */
export function Button(props: ButtonProps | AnchorProps) {
    const { variant = 'primary', size = 'md', fullWidth = false, className = '', children } = props;
    const cls = classes(variant, size, fullWidth, className);

    if ('href' in props && props.href !== undefined) {
        const { variant: _v, size: _s, fullWidth: _f, className: _c, children: _ch, ...rest } = props;
        return (
            <a className={cls} {...rest}>
                {children}
            </a>
        );
    }

    const { variant: _v, size: _s, fullWidth: _f, className: _c, children: _ch, ...rest } = props as ButtonProps;
    return (
        <button className={cls} {...rest}>
            {children}
        </button>
    );
}

export default Button;
